import { useEffect, useRef, useState } from "react";
import { motion, useAnimationFrame } from "framer-motion";
import LivingImage from "@/components/LivingImage";
import { Eyebrow } from "@/components/primitives";

// Six drifting frames after Selected Work. The section moves sideways all
// the time — a slow, editorial marquee that keeps the eye alive whether
// the page is scrolling or resting. Videos play as tiles pass through the
// middle of the viewport (LivingImage's own registry handles that).
// Mobile: no marquee, stacks into a normal grid.
const FRAMES = [
  { still: "S24", film: "F17", type: "Beach · morning" },
  { still: "S25", film: "F18", type: "Terrace · dining" },
  { still: "S27", film: "F20", type: "Destination · desert" },
  { still: "S19", film: null,  type: "Pool · sunset" },
  { still: "S26", film: "F19", type: "Suite · interior" },
  { still: "S10", film: "F12", type: "Landscape · night" },
];

// Duplicate the strip so the animation loops seamlessly. When the first
// copy has scrolled fully out, we snap back by exactly one copy's width and
// nothing on screen changes.
const LOOP = [...FRAMES, ...FRAMES];

export default function HorizontalBand() {
  const trackRef = useRef(null);
  const xRef = useRef(0);
  const halfRef = useRef(0);
  const pausedRef = useRef(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      // half = width of one copy of FRAMES (excluding the duplicate)
      halfRef.current = el.scrollWidth / 2;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Continuous drift. ~40px/second — slow enough to feel editorial.
  useAnimationFrame((_, delta) => {
    if (reduced || pausedRef.current) return;
    const el = trackRef.current;
    if (!el || !halfRef.current) return;
    xRef.current -= (delta / 1000) * 40;
    // seamless wrap
    if (xRef.current <= -halfRef.current) xRef.current += halfRef.current;
    el.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
  });

  return (
    <section
      className="relative overflow-hidden py-16 sm:py-24"
      aria-label="Ambient reel"
      data-testid="horizontal-band"
    >
      <div className="mx-auto mb-10 max-w-[1280px] px-6 sm:px-10">
        <Eyebrow>Ambient reel</Eyebrow>
        <p className="mt-4 max-w-lg text-sm leading-relaxed text-mutedText">
          Six frames drifting sideways as you read down. Each becomes a film
          the moment it settles into the middle of the page.
        </p>
      </div>
      {/* Desktop: continuous marquee */}
      <div
        className="relative hidden overflow-hidden md:block"
        onMouseEnter={() => (pausedRef.current = true)}
        onMouseLeave={() => (pausedRef.current = false)}
      >
        <div
          ref={trackRef}
          className="flex w-max gap-6 will-change-transform"
          style={{ transform: "translate3d(0,0,0)" }}
        >
          {LOOP.map((f, i) => (
            <div
              key={i}
              className="relative aspect-[4/5] w-[26vw] shrink-0 rounded-[2px] border border-hairline"
              data-testid={`band-${i % FRAMES.length}`}
              aria-hidden={i >= FRAMES.length ? "true" : undefined}
            >
              <LivingImage
                still={f.still}
                film={f.film || undefined}
                sizes="26vw"
                wrapperClassName="absolute inset-0"
                className="h-full w-full object-cover"
                testId={`band-${i % FRAMES.length}`}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="on-dark absolute bottom-5 left-5 right-5 flex items-baseline justify-between">
                <span className="display text-lg text-primaryText">{f.type}</span>
                <span className="font-mono text-[9px] tracking-[0.22em] text-mutedText">
                  0{(i % FRAMES.length) + 1}/06
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Mobile: stacked */}
      <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-3 px-6 md:hidden">
        {FRAMES.slice(0, 4).map((f, i) => (
          <div
            key={i}
            className="relative aspect-[4/5] overflow-hidden rounded-[2px] border border-hairline"
          >
            <LivingImage
              still={f.still}
              sizes="50vw"
              wrapperClassName="absolute inset-0"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="on-dark absolute bottom-3 left-3 right-3">
              <span className="display text-sm text-primaryText">{f.type}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
